import {useState, useMemo, useEffect} from 'react';
import PhoneInput from 'react-phone-input-2';
import {useTheme} from '@mui/material';
import 'react-phone-input-2/lib/material.css';
import './telephoneInput.css';
import {isValidPhoneNumber} from 'libphonenumber-js';
import useGeoLocation from 'react-ipgeolocation';

/**
 * Renders a phone input component with a customized style and behavior.
 *
 * @param {Object} props - The component props.
 * @param {string} props.value - The current value of the phone input, should be in E164 format. if it is an empty value please use this: "".
 * @param {function} props.onChange - The callback function to handle value changes. accepts two arguments, the phone number string(if completed it is in e164 format) and a boolean indicating if the number is a valid phone number
 *
 * @returns {JSX.Element} - The rendered phone input component.
 */
export const TelInput = ({value, onChange,customCss, ...props}) => {
  const {disabled, inputStyle, specialLabel, ...otherProps} = props;
  const theme = useTheme();
  const getUserCountryLocation = useGeoLocation();
  const [ userCountryLocation, setUserCountryLocation ] = useState(null)
  const [onFocus, setOnFocus] = useState(false);
  const [country, setCountry] = useState(undefined);
  const mainColor = useMemo(() => {
    return onFocus ? customCss ? customCss.borderColor : theme.palette.primary.main : disabled ? '#BDBDBD' : 'rgba(0, 0, 0, 0.6)';
  }, [onFocus, disabled]);
  const wrapperValue = useMemo(() => {
    if (country === undefined) return value;

    if (value === '') return country.dialCode;
    return value;
  }, [value, country]);

  //fetch geolocation to automatically detect and set phone input country code
  useEffect(() => {
    if (getUserCountryLocation.error) {
      setUserCountryLocation('us'); // fallback if there's an error
    } else if (getUserCountryLocation.country) {
      setUserCountryLocation(getUserCountryLocation.country.toLowerCase());
    }
  }, [getUserCountryLocation]);

  return (
    <PhoneInput
      disabled={disabled}
      disableDropdown={disabled}
      value={wrapperValue}
      onChange={(value, country, _e, formattedValue) => {
        const countryCode = country.countryCode.toUpperCase();
        const isValid = isValidPhoneNumber(formattedValue, countryCode);
        const outputValue = value === country.dialCode ? '' : `+${value}`;
        setCountry(country);
        onChange(outputValue, isValid);
      }}
      onFocus={() => {
        setOnFocus(true);
      }}
      onBlur={() => {
        setOnFocus(false);
      }}
      containerStyle={{
        color:  customCss ? customCss.color : mainColor,
        borderColor:  customCss ? customCss.color : mainColor,
        fontFamily: theme.typography.fontFamily,
        fontWeight: 400,
        userSelect: 'none',
      }}
      inputStyle={{
        color: customCss ? customCss.color : undefined,
        borderRadius: customCss ? customCss.borderRadius : undefined,
        borderColor: customCss ?  customCss.borderColor  : undefined,
        border: customCss ? customCss.border : undefined,
        // Apply background color conditionally based on focus and customCss
        background: onFocus 
          ? (customCss && customCss.backgroundColor ? customCss.backgroundColor : undefined) // Red background when focused and customCss.backgroundColor is not present
          : undefined,
        ...(onFocus && {
          boxShadow: customCss ?  'none' : `0 0 0 1px ${theme.palette.primary.main}`,
          borderColor: customCss ? customCss.borderColor :  theme.palette.primary.main,
          
        }),
        width: '100%',
        ...(disabled && {
          boxShadow: customCss ?  'none' : `0 0 0 1px ${theme.palette.primary.main}`,
          borderColor: customCss ? customCss.borderColor :  'rgba(205,205,205,255)',
          backgroundColor: 'white',
          // borderColor: 'rgba(205,205,205,255)',
          opacity: 0.7,
        }),
        "&:hover" : {
          boxShadow: customCss ? "0px 8px 18px 0px rgba(0, 0, 0, 0.10)" : undefined, // Light shadow on hover
         },

        ...inputStyle,
      }}
      dropdownStyle={{
        color: 'black',
        fontSize: '.8rem',
      }}
      searchStyle={{
        width: '85%',
        margin: '0 auto',
        border: 'none',
        fontFamily: theme.typography.fontFamily,
      }}
      country={userCountryLocation ?? 'us'} 
      specialLabel={specialLabel ?? 'Phone Number'}
      countryCodeEditable={false}
      preferredCountries={['ca', 'us']}
      searchPlaceholder="Search"
      enableSearch
      disableSearchIcon
      {...otherProps}
    />
  );
};
